import React from "react";
import { Avatar, Divider, IconButton, Link, Stack, Typography, TypographyProps } from "@mui/material";
import styled from "@emotion/styled";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LanguageIcon from "@mui/icons-material/Language";

import { CrunchbaseData } from "store/types";
import { DealRoleGuard } from "components/guards";
import { RoleName } from "../../../constants";

interface Props {
  reconnect: () => void;
  data: CrunchbaseData;
}

const oneRowTextStyle: TypographyProps["sx"] = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden"
};

const StyledDivider = styled(Divider)(() => ({
  backgroundColor: "#DAE0E2",
  height: "1px"
}));

const ContactRow = styled(Stack)(() => ({
  flexDirection: "row" as const,
  gap: "10px",
  fontSize: "14px",
  fontWeight: 400,
  alignItems: "center"
}));

const BasicText = styled(Typography)(() => ({
  fontSize: "14px",
  fontWeight: 400,
  color: "#0A3142"
}));

const Title = styled(Typography)(() => ({
  fontSize: "16px",
  fontWeight: 700,
  color: "#0A3142"
}));

export const Content: React.FC<Props> = ({
  reconnect,
  data: { logo, name, link, locations, website, description, industries, employees, founders, similar_companies }
}) => {
  return (
    <Stack sx={{ gap: "24px", alignItems: "stretch", overflow: "hidden" }}>
      <Stack direction="row" gap="8px" sx={{ overflow: "hidden" }}>
        <Avatar src={logo} sx={{ width: "52px", height: "52px", borderRadius: 0 }} />
        <Stack sx={{ overflow: "hidden" }}>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#0A3142", ...oneRowTextStyle }}>
            {name}
          </Typography>
          <Stack direction="row" gap="8px" overflow="hidden">
            <Typography
              target="_blank"
              href={link}
              noWrap
              sx={{
                color: "#5D7782",
                fontSize: "13px",
                fontWeight: 400,
                ...oneRowTextStyle
              }}
              component={Link}
            >
              {link}
            </Typography>
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <IconButton onClick={reconnect} sx={{ alignSelf: "flex-start", width: "20px", height: "20px" }}>
                <EditOutlinedIcon sx={{ fontSize: "18px", color: "#0A3142" }} />
              </IconButton>
            </DealRoleGuard>
          </Stack>
        </Stack>
      </Stack>
      <StyledDivider />
      <Stack gap="5px">
        <ContactRow>
          <PlaceOutlinedIcon sx={{ color: "#0A3142" }} />
          <BasicText sx={oneRowTextStyle}>{locations}</BasicText>
        </ContactRow>
        <ContactRow>
          <PeopleAltOutlinedIcon sx={{ color: "#0A3142" }} />
          <BasicText sx={oneRowTextStyle}>{employees}</BasicText>
        </ContactRow>
        <ContactRow>
          <LanguageIcon sx={{ color: "#0A3142" }} />
          <Typography
            target="_blank"
            href={website}
            noWrap
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#0A3142",
              textDecoration: "none",
              ...oneRowTextStyle
            }}
            component={Link}
          >
            {website}
          </Typography>
        </ContactRow>
      </Stack>
      <StyledDivider />
      <Stack sx={{ gap: "5px" }}>
        <Title sx={oneRowTextStyle}>Description</Title>
        <BasicText>{description}</BasicText>
      </Stack>
      <Stack sx={{ gap: "5px" }}>
        <Title sx={oneRowTextStyle}>Industries</Title>
        <BasicText>{industries}</BasicText>
      </Stack>
      <Stack gap="12px">
        <Title>Company founders</Title>
        <Stack direction="row" flexWrap="wrap" gap="10px">
          {founders?.map(({ name, logo }, index) => (
            <Stack key={index} direction="row" gap="10px" width="48%">
              <Avatar src={logo} sx={{ width: "46px", height: "46px" }} />
              <Stack p="6px 0" overflow="hidden">
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0A3142", ...oneRowTextStyle }}>
                  {name}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Stack gap="12px">
        <Title>Competitor and similar companies</Title>
        <Stack direction="row" gap="14px" overflow="hidden" flexWrap="wrap">
          {similar_companies?.map(({ name, logo }, index) => (
            <Stack
              key={index}
              direction="row"
              sx={{ gap: "14px", alignItems: "center", maxWidth: "48%", minWidth: "48%" }}
            >
              <Avatar src={logo} sx={{ borderRadius: "5px", width: "35px", height: "35px" }} />
              <Typography sx={{ fontSize: "14px", flexWrap: 700, color: "#0A3142" }}>{name}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
